import {NumberRangeValue, putAll} from '@fl/cmsch-fe-library';
import {change} from 'redux-form';
import {SagaIterator} from 'redux-saga';

import {CriteriaDefinition} from 'api/gen/CriteriaDefinition';

import {Criterias} from '../../types/criterias';
import {matingBullsFilterFormName} from '../../types/mating-bulls-filter-form-values';
import {matingCowsFilterFormName} from '../../types/mating-cows-filter-form-values';
import {getDefaultCriteriaValues} from '../../utils/get-default-criteria-values';

/** Supported form names for criteria resets. */
export type MatingFormName = typeof matingCowsFilterFormName | typeof matingBullsFilterFormName;

const getNumberRangeResetValue = (
    value: NumberRangeValue | undefined,
    deactivateNumberRange: boolean,
): NumberRangeValue | undefined => deactivateNumberRange && value
    ? {...value, selectedType: null}
    : value;

const getCriteriaResetAction = (
    formName: MatingFormName,
    criterion: CriteriaDefinition,
    defaultValues: Criterias,
    deactivateNumberRange: boolean,
): ReturnType<typeof change> => {
    if (criterion.tag === 'BOOL') {
        return change(formName, `criterias.radios.${criterion.code}`, defaultValues.radios[criterion.code]);
    } else if (criterion.tag === 'DATE') {
        return change(
            formName,
            `criterias.dateRanges.${criterion.code}`,
            deactivateNumberRange ? [null, null] : defaultValues.dateRanges[criterion.code],
        );
    }

    return change(
        formName,
        `criterias.numberRanges.${criterion.code}`,
        getNumberRangeResetValue(defaultValues.numberRanges[criterion.code], deactivateNumberRange),
    );
};

/**
 * Resets all criteria values for the requested form and accordion.
 *
 * @param formName Form name used by the current workflow.
 * @param criteriasOfAccordion Criteria definitions in the current accordion.
 * @param deactivateNumberRange Whether a single number-range reset should clear its selected mode.
 */
export function* resetCriteriaValues(
    formName: MatingFormName,
    criteriasOfAccordion: Array<CriteriaDefinition>,
    deactivateNumberRange = false,
): SagaIterator {
    const defaultCriterionValues = getDefaultCriteriaValues(criteriasOfAccordion);
    const changeActions = criteriasOfAccordion.map(criterion => getCriteriaResetAction(
        formName,
        criterion,
        defaultCriterionValues,
        deactivateNumberRange,
    ));

    yield putAll(changeActions);
}
